import { MeasurementController } from ".";
import {Circle as CircleStyle, Fill, Stroke, Style} from "ol/style";
import {Vector as VectorSource} from "ol/source";
import {Vector as VectorLayer} from "ol/layer";
import {transform} from 'ol/proj';
import {Draw} from "ol/interaction";
import {getArea, getLength, getDistance} from 'ol/sphere';
import Overlay from 'ol/Overlay';
import {unByKey} from 'ol/Observable';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import LineString from 'ol/geom/LineString';
import './aerial-tooltip.css';
import { setAerialTooltip } from "../../viewer";
import { t } from "../../../localization";
import { MeasureType } from "../../../types/measurements";

export class MinimapMeasure {
  private controller: MeasurementController;
  private mapSource: VectorSource<any>;
  private mapLayer: VectorLayer<any>;
  private styleHover: Style;
  private styleDefault: Style;
  private drawStyle: Style;

  public enabled = false;
  public measurementType: MeasureType;
  public sketch = null;
  private draw = null;
  private initialized = false;
  private tempTooltips = [];
  private measurements = [];

  constructor(controller) {
    this.controller = controller;

    this.styleHover = new Style({
      fill: new Fill({
        color: 'rgba(255, 255, 255, 0.2)'
      }),
      image: new CircleStyle({
        radius: 7,
        fill: new Fill({
          color: '#008B8B'
        })
      }),
      stroke: new Stroke({
        color: "#ffcc33",
        width: 2
      })
    });

    this.styleDefault = new Style({
      fill: new Fill({
        color: 'rgba(255, 255, 255, 0.2)'
      }),
      image: new CircleStyle({
        radius: 7,
        fill: new Fill({
          color: '#008B8B'
        })
      }),
      stroke: new Stroke({
        color: "#008B8B",
        width: 2
      })
    });

    this.drawStyle = new Style({
      fill: new Fill({
        color: 'rgba(255, 255, 255, 0.2)'
      }),
      stroke: new Stroke({
        color: 'rgba(0, 0, 0, 0.5)',
        lineDash: [10, 10],
        width: 2
      }),
      image: new CircleStyle({
        radius: 5,
        stroke: new Stroke({
          color: 'rgba(0, 0, 0, 0.7)'
        }),
        fill: new Fill({
          color: 'rgba(255, 255, 255, 0.2)'
        })
      })
    });
  }

  get map() {
    return this.controller.viewer.minimap.map;
  }

  get mapElement() {
    return this.controller.viewer.minimap.containerElement;
  }

  get drawingType() {
    return (this.measurementType === MeasureType.Area)
      ? "Polygon"
      : "LineString";
  }

  get moreTooltip() {
    let coordinates = this.sketch.getGeometry().flatCoordinates;
    let numPoints = (coordinates.length / 2.0) - 1;

    let canFinish = (this.measurementType === MeasureType.Area)
      ? numPoints > 2
      : true;

    if (canFinish) {
      return [
        t("measure.click-to-add-more-points", {
          count: numPoints
        }),
        t('measure.double-click-to-finish'),
        t('measure.right-click-to-cancel')
      ];
    }

    return [
      t("measure.click-to-add-more-points", {
        count: numPoints
      }),
      t('measure.right-click-to-cancel')
    ];
  }

  get startTooltip() {
    if (this.measurementType === MeasureType.Length) {
      return [t('measure.click-to-start-drawing-line')];
    }

    if (this.measurementType === MeasureType.Area) {
      return [t('measure.click-to-start-drawing-polygon')];
    }

    return [t('measure.invalid-measure-type')];
  }

  get formatter() {
    return this.controller.formatter;
  }

  init() {
    if (this.initialized) return;

    this.mapSource = new VectorSource({
      wrapX: false
    });

    this.mapLayer = new VectorLayer({
      zIndex: 1,
      source: this.mapSource,
      style: feature => {
        return feature.get("hover")
          ? this.styleHover
          : this.styleDefault;
      },
      properties: {
        type: "measure"
      }
    });

    this.map.addLayer(this.mapLayer);

    this.addInteraction();
    this.initEvents();
    this.initialized = true;
  }

  initEvents() {
    this.mapElement.addEventListener('mouseenter', () => {
      if (this.controller.enabled) {
        this.reloadInteraction();
      }
    });

    this.mapElement.addEventListener('mouseleave', () => {
      if (this.controller.enabled) {
        this.removeInteraction();
      }
    });
  }

  render(measurement) {
    this.removeByID(measurement.id);

    const geometry = measurement.geometry;
    const feature = new Feature(geometry);
    feature.set("hover", false);

    const tooltips = this.getTooltipsForGeometry(geometry);
    this.mapSource.addFeature(feature);

    this.measurements.push({
      id: measurement.id,
      feature,
      tooltips
    });
  }

  getByID(id) {
    return this.measurements.find(measurement => measurement.id === id);
  }

  removeByID(id) {
    const measurement = this.getByID(id);
    if (!measurement) return;

    const {feature, tooltips} = measurement;

    this.mapSource.removeFeature(feature);
    tooltips.forEach(tooltip => {
      this.map.removeOverlay(tooltip);
    });

    this.measurements = this.measurements
      .filter(measurement => measurement.id !== id);
  }

  removeAll() {
    [...this.measurements].forEach(measurement => {
      this.removeByID(measurement.id);
    });

    this.measurements = [];
  }

  setHover(id, hover) {
    const measurement = this.getByID(id);
    if (!measurement) return;

    measurement.feature.set("hover", hover);
    measurement.tooltips.forEach(tooltip => {
      const element = tooltip.element;
      const child = element.children[0];

      if (hover) {
        child.classList.add("tooltip-hover");
      } else {
        child.classList.remove("tooltip-hover");
      }
    });
  }

  setMeasurementType(measurementType: MeasureType) {
    const validMeasureType = (measurementType === MeasureType.Length)
      || (measurementType === MeasureType.Area);

    this.measurementType = validMeasureType
      ? measurementType
      : null;

    this.reloadInteraction();

    if (this.measurementType === null) {
      this.removeInteraction();
    }
  }

  onMouseMove(event) {
    if (event.dragging || !this.controller.enabled) {
      return;
    }

    let helpMsg = this.sketch
      ? this.moreTooltip
      : this.startTooltip;

    setAerialTooltip(helpMsg);
  }

  getTooltipsForGeometry(geometry) {
    const tooltips = [];

    // Per segment tooltips
    const segments = this.getSegmentInfo(geometry);
    segments.forEach(segment => {
      const {position, message} = segment;
      const {element, tooltip} = this.addNewTooltip(false);
      tooltip.setPosition(position);
      element.innerHTML = message;
      tooltips.push(tooltip);
    });

    // Additional tooltip for total length / area
    let objectMessage;
    let objectPosition;

    // Total line length
    if (geometry instanceof LineString) {
      const length = getLength(geometry);
      objectMessage = this.formatter.formatLength(length);
      objectPosition = geometry.getLastCoordinate();
    }

    // Total area
    const hasEnoughSegments = segments.length >= 3;
    if ((geometry instanceof Polygon) && hasEnoughSegments) {
      const area = getArea(geometry);
      objectMessage = this.formatter
        .formatArea(area);
      objectPosition = geometry
        .getInteriorPoint()
        .getCoordinates();
    }

    if (objectPosition) {
      const {element, tooltip} = this.addNewTooltip(true);
      tooltip.setPosition(objectPosition);
      element.innerHTML = objectMessage;
      tooltips.push(tooltip);
    }

    return tooltips;
  }

  clearTempTooltips() {
    this.tempTooltips.forEach(tooltip => {
      this.map.removeOverlay(tooltip);
    });

    this.tempTooltips = [];
  }

  reloadInteraction() {
    if (!this.map) return;

    if (this.draw) {
      this.map.removeInteraction(this.draw);
    }

    this.sketch = null;
    this.addInteraction();
  }

  removeInteraction() {
    if (!this.draw) return;

    this.clearTempTooltips();
    this.map.removeInteraction(this.draw);
  }

  addInteraction() {
    if (this.measurementType === null || this.map === null) return;
    if (!this.controller.editable) return;

    this.removeInteraction();

    this.draw = new Draw({
      type: this.drawingType,
      condition: event => {
        if (event.originalEvent.button === 0) {
          return true;
        }

        if (event.originalEvent.button === 2) {
          if (this.controller.enabled) {
            this.reloadInteraction();
            setAerialTooltip(this.startTooltip);
          }
        }

        return false;
      },
      style: this.drawStyle
    });

    this.map.addInteraction(this.draw);

    let listener;

    this.draw.on('drawstart', event => {
      this.sketch = event.feature;
      this.clearTempTooltips();

      const geometry = this.sketch.getGeometry();
      listener = geometry.on('change', () => {
        this.clearTempTooltips();
        this.tempTooltips = this.getTooltipsForGeometry(geometry);
      });
    }, this);

    this.draw.on('drawend', (event) => {
      this.clearTempTooltips();

      this.sketch = null;
      unByKey(listener);

      const geometry = event.feature.getGeometry();
      this.controller.addAerialMeasurement(
        geometry, this.measurementType);

      setAerialTooltip(this.startTooltip);
    }, this);
  }

  addNewTooltip(alternateColor) {
    const element = document.createElement('div');
    element.className = "tooltip tooltip-minimap";

    element.className = alternateColor
      ? "tooltip tooltip-minimap tooltip-alternate"
      : "tooltip tooltip-minimap";

    const tooltip = new Overlay({
      element,
      positioning: "center-center",
      className: "ol-overlay-container tooltip-container"
    });

    this.map.addOverlay(tooltip);
    return {tooltip, element};
  }

  getSegmentInfo(geometry) {
    const transformed = geometry.clone().transform('EPSG:3857', 'EPSG:4326');
    const flatCoordinates = transformed.flatCoordinates;
    const numPoints = flatCoordinates.length / 2;

    const segments = [];
    for (let i=0; i<numPoints-1; i++) {
      const index0 = 2 *i;
      const index1 = 2*(i+1);

      const P1 = flatCoordinates.slice(index0, index0 + 2);
      const P2 = flatCoordinates.slice(index1, index1 + 2);

      const length = getDistance(P1, P2);
      if (length === 0) continue;

      const x = (P1[0] + P2[0]) / 2.0;
      const y = (P1[1] + P2[1]) / 2.0;

      const position = transform([x, y], 'EPSG:4326', 'EPSG:3857');
      const message = this.formatter.formatLength(length);

      segments.push({position, message});
    }

    return segments;
  }
}
